import React, {PureComponent} from 'react';
import styled from 'styled-components';

import Input from '../../components/Input';
import JsonLogicBuilder from '../../components/JsonLogicBuilder';

import SubConfiguration from './SubConfiguration';
import {getLogicBuilderVariables, parseDefaultAnswerFormula, FORMULA_DEFAULT_ANSWERS_ENABLED} from './shared';

const TEXT_DEFAULT_ANSWER_TYPES = {
  SUBMITTER_USERNAME: '{{SUBMITTER_USERNAME}}',
  SUBMITTER_EMAIL: '{{SUBMITTER_EMAIL}}',
  CUSTOM: 'CUSTOM',
};

const NUMERIC_DEFAULT_ANSWER_TYPES = {
  NUMBER: 'NUMBER',
  FORMULA: 'FORMULA',
};

// Restrict the options for expressions as Numeric defaults only need math, numbers and variables
const FORMULA_EXPRESSION_TYPES = ['FORMULA', 'VARIABLE', 'STATIC_NUMBER'];

// A cross row total is only meaningful on a repeated question: it aggregates an answer over every row of
// the sheet, and sheet setup is answered once before any row exists.
const ROW_FORMULA_EXPRESSION_TYPES = [...FORMULA_EXPRESSION_TYPES, 'AGGREGATE'];

const BLANK_FORMULA = {'+': [null, null]}; // default math formula

function getTextDefaultAnswerType(value) {
  if (value === TEXT_DEFAULT_ANSWER_TYPES.SUBMITTER_USERNAME) return 'SUBMITTER_USERNAME';
  if (value === TEXT_DEFAULT_ANSWER_TYPES.SUBMITTER_EMAIL) return 'SUBMITTER_EMAIL';
  if (value) return 'CUSTOM';
  return '';
}

class DefaultAnswers extends PureComponent {
  constructor(props) {
    super(props);

    const defaultAnswers = props.question.defaultAnswers || [];
    const currentValue = defaultAnswers[0] || '';
    const answerType = getTextDefaultAnswerType(currentValue);
    const formula = parseDefaultAnswerFormula(currentValue);

    this.state = {
      textDefaultType: answerType,
      customTextValue: answerType === 'CUSTOM' ? currentValue : '',
      numericDefaultType: formula
        ? NUMERIC_DEFAULT_ANSWER_TYPES.FORMULA
        : NUMERIC_DEFAULT_ANSWER_TYPES.NUMBER,
      numericValue: formula ? '' : currentValue,
      formula,
    };
  }

  componentDidUpdate(prevProps) {
    const prevDefaults = prevProps.question.defaultAnswers || [];
    const currentDefaults = this.props.question.defaultAnswers || [];
    const prevValue = prevDefaults[0] || '';
    const currentValue = currentDefaults[0] || '';

    if (prevValue !== currentValue) {
      const answerType = getTextDefaultAnswerType(currentValue);
      const formula = parseDefaultAnswerFormula(currentValue);

      this.setState(state => ({
        textDefaultType: answerType,
        customTextValue: answerType === 'CUSTOM' ? currentValue : '',
        // Keep the type the user picked when the value is cleared (e.g. the formula was deleted) so the
        // form does not jump back to the plain number input.
        numericDefaultType: currentValue
          ? (formula ? NUMERIC_DEFAULT_ANSWER_TYPES.FORMULA : NUMERIC_DEFAULT_ANSWER_TYPES.NUMBER)
          : state.numericDefaultType,
        numericValue: formula ? state.numericValue : currentValue,
        formula: currentValue ? formula : null,
      }));
    }
  }

  handleTextTypeChange = (e) => {
    const type = e.target.value;
    const {onChange} = this.props;

    this.setState({textDefaultType: type});

    if (type === 'SUBMITTER_USERNAME') {
      onChange([TEXT_DEFAULT_ANSWER_TYPES.SUBMITTER_USERNAME]);
    } else if (type === 'SUBMITTER_EMAIL') {
      onChange([TEXT_DEFAULT_ANSWER_TYPES.SUBMITTER_EMAIL]);
    } else if (type === 'CUSTOM') {
      onChange([this.state.customTextValue]);
    } else {
      onChange([]);
    }
  };

  handleNumericTypeChange = (e) => {
    const type = e.target.value;
    const {onChange} = this.props;
    const {numericValue, formula} = this.state;

    if (type === NUMERIC_DEFAULT_ANSWER_TYPES.FORMULA) {
      // Start from a blank formula so the builder opens on a math operation instead of an empty picker.
      const nextFormula = formula || BLANK_FORMULA;
      this.setState({numericDefaultType: type, formula: nextFormula});
      onChange([JSON.stringify(nextFormula)]);
    } else {
      this.setState({numericDefaultType: type});
      onChange(numericValue ? [numericValue] : []);
    }
  };

  handleFormulaChange = (formula) => {
    this.setState({formula});
    this.props.onChange(formula ? [JSON.stringify(formula)] : []);
  };

  handleNumericValueChange = (e) => {
    const value = e.target.value;
    this.setState({numericValue: value});
    this.props.onChange([value]);
  };

  handleCustomTextChange = (e) => {
    const value = e.target.value;
    this.setState({customTextValue: value});
    this.props.onChange([value]);
  };

  renderNumericBuilder() {
    const {form, catalog, question} = this.props;
    const {numericDefaultType, numericValue, formula} = this.state;

    return (
      <React.Fragment>
        <Input
          type="select"
          value={numericDefaultType}
          onChange={this.handleNumericTypeChange}>
          <option value={NUMERIC_DEFAULT_ANSWER_TYPES.NUMBER}>Number</option>
          <option value={NUMERIC_DEFAULT_ANSWER_TYPES.FORMULA}>Formula</option>
        </Input>
        {numericDefaultType === NUMERIC_DEFAULT_ANSWER_TYPES.FORMULA ? (
          <Formula>
            <JsonLogicBuilder
              expand
              variables={getLogicBuilderVariables(form, catalog, question, {
                excludeMultiValue: true,
                excludeFormulaReferences: true,
                // A sheet setup default answer can only be worked out from other sheet setup answers.
                // Row questions keep both sides, since sheet setup is always answered before any row.
                excludeRowQuestions: !!question.context
              })}
              expressionTypes={question.context ? FORMULA_EXPRESSION_TYPES : ROW_FORMULA_EXPRESSION_TYPES}
              value={formula}
              onChange={this.handleFormulaChange}
            />
          </Formula>
        ) : (
          <Input
            type="number"
            step="any"
            placeholder="Enter the default answer"
            defaultValue={numericValue}
            onBlur={this.handleNumericValueChange}
            style={{marginTop: 8}}
          />
        )}
      </React.Fragment>
    );
  }

  renderForm() {
    const {question, onChange} = this.props;
    const {defaultAnswers} = question;

    switch (question.type) {
      case 'NUMERIC':
      case 'MEASURED_NUMERIC':
        // Formula default answers are held back until the mobile app supports them, so the number input
        // is all a user can set. Delete this branch when FORMULA_DEFAULT_ANSWERS_ENABLED is turned on.
        if (!FORMULA_DEFAULT_ANSWERS_ENABLED) {
          return (
            <Input
              type="number"
              step="any"
              placeholder="Enter the default answer"
              defaultValue={defaultAnswers[0]}
              onBlur={e => onChange([e.target.value])}
            />
          );
        }

        return this.renderNumericBuilder();

      case 'BOOLEAN':
        return (
          <Input
            type="select"
            value={defaultAnswers[0]}
            onChange={e => onChange([e.target.value])}>
            <option>Choose the default answer</option>
            <option value="true">Yes (True)</option>
            <option value="false">No (False)</option>
          </Input>
        );

      case 'TEXT':
        return (
          <React.Fragment>
            <Input
              type="select"
              value={this.state.textDefaultType}
              onChange={this.handleTextTypeChange}>
              <option value="">Choose a default answer type</option>
              <option value="SUBMITTER_USERNAME">Submitter's Username</option>
              <option value="SUBMITTER_EMAIL">Submitter's Email</option>
              <option value="CUSTOM">Custom Text</option>
            </Input>
            {this.state.textDefaultType === 'CUSTOM' && (
              <Input
                type="text"
                placeholder="Enter the default answer"
                value={this.state.customTextValue}
                onChange={this.handleCustomTextChange}
                style={{marginTop: 8}}
              />
            )}
          </React.Fragment>
        );

      // Inventory questions can't have a default answer, so there is nothing to set.
      case 'INVENTORY_RECEIVING':
      case 'INVENTORY_USAGE':
        return null;

      default:
        throw new Error(`Unhandled default answer type: ${question.type}`)
    }
  }

  render() {
    return (
      <SubConfiguration>
        <SubConfiguration.Item>
          {this.renderForm()}
        </SubConfiguration.Item>
      </SubConfiguration>
    );
  }
}

const Formula = styled.div`
  margin-top: 0.5rem;
`;

export default DefaultAnswers;